import axios from "axios";
import { useEffect, useState } from "react";
import { Button, Card, Col, Modal, Row, Table } from "react-bootstrap";
import { FaBookOpen, FaQuestionCircle } from "react-icons/fa";
import { SiImessage } from "react-icons/si";
import { useLocation, useNavigate } from "react-router-dom";
import Pagination from "../../Pagination";
import ChatComponent from "../ChatComponent";
import Note from "./Note";
import Slider from "./Slider";

const DreamApplication = () => {
  const BASE_API_URL = process.env.REACT_APP_API_URL;
  const location = useLocation();
  const navigate = useNavigate();

  const userName = location.state?.userName;
  const userEmail = location.state?.userEmail;
  const currentDreamAppPage = location.state?.currentDreamAppPage || 0;
  const currentDreamAppPageSize = location.state?.currentDreamAppPageSize || 20;
  const dreamAppStatus = location.state?.dreamAppStatus || 'all';
  const dreamAppFromDate = location.state?.dreamAppFromDate || '';
  const dreamAppToDate = location.state?.dreamAppToDate || '';
  const dreamAppSearch = location.state?.dreamAppSearch || '';

  const [companyName, setCompanyName] = useState('');
  const [applications, setApplications] = useState([]);
  const [filterStatus, setFilterStatus] = useState(dreamAppStatus);
  const [resumeTypes, setResumeTypes] = useState({});
  const [fileNames, setFileNames] = useState({});
  const [fromDate, setFromDate] = useState(dreamAppFromDate);
  const [toDate, setToDate] = useState(dreamAppToDate);
  const [page, setPage] = useState(currentDreamAppPage);
  const [totalPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(currentDreamAppPageSize);
  const [search, setSearch] = useState(dreamAppSearch);
  const [loading, setLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isGetUserData, setGetUserData] = useState(true);
  const [userData, setUserData] = useState({});
  const [isResumeBrief, setIsResumeBrief] = useState(false);

  const [candidateName, setCandidateName] = useState({});
  const [candidateEmail, setCandidateEmail] = useState({});
  const [hrName, setHrName] = useState({});
  const [hrEmail, setHrEmail] = useState({});
  const [unreadMessages, setUnreadMessages] = useState({});

  const [showQAModal, setShowQAModal] = useState(false);
  const [selectedQA, setSelectedQA] = useState({});

  const [isChatOpen, setIsChatOpen] = useState(false);
  const [chatData, setChatData] = useState({ applicationId: null, candidateId: null, hrId: null });

  const [isNoteOpen, setIsNoteOpen] = useState(false);
  const [selectedApplicationId, setSelectedApplicationId] = useState();
  const [selectedCandidateName, setSelectedCandidateName] = useState('');

  const [showBriefSettings, setShowBriefSettings] = useState(false);
  const [showMessage, setShowMessage] = useState(false);

  const user = { userName, userEmail };

  // ------------------- Initialization -------------------
  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem('user'));
    if (storedUser?.companyName) setCompanyName(storedUser.companyName);
  }, [location]);

  useEffect(() => {
    if (!search && filterStatus === 'all' && !(fromDate && toDate)) {
      fetchApplications();
    } else if (filterStatus || (fromDate && toDate)) {
      handleSelect(filterStatus, fromDate, toDate);
    } else if (search) {
      fetchApplicationBySearch(search);
    }
    fetchUserData(userEmail);
  }, [userEmail, page, pageSize, search, filterStatus, fromDate, toDate]);

  useEffect(() => {
    fetchCandidateDetails();
    fetchResumeTypes(applications);
  }, [applications]);

  useEffect(() => {
    fetchUnreadMessages();
  }, [applications]);

  // ------------------- Fetch Functions -------------------
  const fetchApplications = async () => {
    try {
      setLoading(true);
      const params = { userEmail, page, size: pageSize };
      const response = await axios.get(`${BASE_API_URL}/getDreamApplicationsByCompanyWithQA`, { params });
      setApplications(response.data);
      setTotalPages(response.data.totalPages || 1);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const fetchApplicationBySearch = async (search) => {
    try {
      const params = { userEmail, page, size: pageSize, search };
      const response = await axios.get(`${BASE_API_URL}/getDreamApplicationsByCompanyBySkills`, { params });
      setApplications(response.data.content);
      setTotalPages(response.data.totalPages);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = async (filterStatus, fromDate, toDate) => {
    try {
      setLoading(true);
      const jobId = 0;
      const params = { jobId, filterStatus, userEmail, page, size: pageSize };
      if (fromDate && toDate) { params.fromDate = fromDate; params.toDate = toDate; }
      if (search) params.search = search;
      const endpoint = fromDate && toDate
        ? `${BASE_API_URL}/getFilterDreamApplicationsWithDateByCompany`
        : `${BASE_API_URL}/getFilterDreamApplicationsByCompany`;
      const response = await axios.get(endpoint, { params });
      setApplications(response.data.content);
      setTotalPages(response.data.totalPages);
    } catch (error) {
    } finally { setLoading(false); }
  };

  const fetchUserData = async (userEmail) => {
    try {
      const response = await axios.get(`${BASE_API_URL}/getHRName?userEmail=${userEmail}`);
      setUserData(response.data);
    } catch (error) { }
  };

  const fetchCandidateDetails = async () => {
    setGetUserData(true);
    const candidateNames = {};
    const candidateEmails = {};
    const hrNames = {};
    const hrEmails = {};
    try {
      for (const item of applications) {
        const app = item.application;

        const resCandidate = await axios.get(`${BASE_API_URL}/getUserName`, { params: { userId: app.candidateId } });
        candidateNames[app.candidateId] = resCandidate.data.userName;
        candidateEmails[app.candidateId] = resCandidate.data.userEmail;

        const resHR = await axios.get(`${BASE_API_URL}/getUserName`, { params: { userId: app.hrId } });
        hrNames[app.hrId] = resHR.data.userName;
        hrEmails[app.hrId] = resHR.data.userEmail;
      }
    } catch (error) { } finally { setLoading(false); }
    setCandidateName(candidateNames);
    setCandidateEmail(candidateEmails);
    setHrName(hrNames);
    setHrEmail(hrEmails);
    setGetUserData(false);
  };

  const fetchResumeTypes = async (apps) => {
    setIsLoading(true);
    const types = {};
    const fileNamesObj = {};
    for (const item of apps) {
      const app = item.application;
      try {
        const response = await axios.get(`${BASE_API_URL}/getResumeByApplicationId?resumeId=${app.resumeId}`);
        types[app.resumeId] = response.data.fileType;
        fileNamesObj[app.resumeId] = response.data.fileName;
      } catch (error) { } finally { setLoading(false); }
    }
    setResumeTypes(types);
    setFileNames(fileNamesObj);
    setIsLoading(false);
  };

  const fetchUnreadMessages = async () => {
    if (!applications || applications.length === 0) { setUnreadMessages({}); return; }
    const unread = {};
    for (const item of applications) {
      const app = item.application;
      try {
        const count = await fetchCountUnreadMessage(app.applicationId);
        unread[app.applicationId] = count || 0;
      } catch (error) { }
    }
    setUnreadMessages(unread);
  };

  const fetchCountUnreadMessage = async (applicationId) => {
    try {
      const response = await axios.get(`${BASE_API_URL}/fetchCountUnreadMessageForHRByApplicationId`, { params: { applicationId, userEmail } });
      return response.data;
    } catch (error) { return 0; }
  };

  // ------------------- Resume & Download -------------------
  const renderResumeComponent = (resumeId) => {
    const fileType = resumeTypes[resumeId];
    const fileName = fileNames[resumeId];

    if (fileType === 'file') {
      return <Button onClick={() => { handleDownload(resumeId, fileName); updateViewCount(resumeId, "file"); }}>Download</Button>;
    } else if (fileType === 'link') {
      return <a href={fileName} target="_blank" rel="noopener noreferrer" onClick={() => updateViewCount(resumeId, "link")}>Click here</a>;
    } else if (fileType === 'brief') {
      return <Button onClick={() => { updateViewCount(resumeId, "brief"); openPopup(fileName); }}>Open Brief</Button>;
    } else { return null; }
  };

  const handleDownload = async (resumeId, fileName) => {
    const action = "download";
    const jobId = 0;
    try {
      await axios.post(`${BASE_API_URL}/resume-increment-view`, null, { params: { resumeId, action, companyName, jobId } });
      const response = await axios.get(`${BASE_API_URL}/downloadResume?resumeId=${resumeId}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url; link.setAttribute('download', fileName); document.body.appendChild(link); link.click();
    } catch (error) { } finally { setLoading(false); }
  };

  const updateViewCount = async (resumeId, action) => {
    try {
      const jobId = 0;
      await axios.post(`${BASE_API_URL}/resume-increment-view`, null, { params: { resumeId, action, companyName, jobId } });
    } catch (error) { }
  };

  const openPopup = (fileName) => { setShowMessage(fileName); setShowBriefSettings(true); };

  // ------------------- Handlers -------------------
  const handleFilterChange = (e) => { setFilterStatus(e.target.value); handleSelect(e.target.value, fromDate, toDate); };
  const handleSearchChange = (e) => setSearch(e.target.value);
  const handlePageClick = (data) => setPage(data.selected);
  const handlePageSizeChange = (e) => { setPageSize(parseInt(e.target.value)); setPage(0); };
  const handleFromDateChange = (date) => { setFromDate(date); handleSelect(filterStatus, date, toDate); };
  const handleToDateChange = (date) => { setToDate(date); handleSelect(filterStatus, fromDate, date); };
  const handleClick = (candidateId) => { };
  const handleNoteClick = async (applicationId, candidateId, candidateName1, resumeId) => {
    setSelectedApplicationId(applicationId);
    const candidate = candidateName[candidateId];
    try {
      const response = await axios.get(`${BASE_API_URL}/getResumeByApplicationId?resumeId=${resumeId}`);
      if (response.data.fileType === 'brief') setSelectedCandidateName('Ninja Applicant');
      else setSelectedCandidateName(candidateName1 || candidate);
    } catch (error) { setSelectedCandidateName(candidateName1 || candidate); }
    setIsNoteOpen(true);
  };

  const updateStatus = async (applicationId, newStatus, hrId) => {
    try { await axios.put(`${BASE_API_URL}/updateApplicationStatus?applicationId=${applicationId}&newStatus=${newStatus}&hrEmail=${userEmail}`); fetchApplications(); } catch (error) { }
  };

  const toggleChat = async (item) => {
    const app = item.application;
    try { await axios.put(`${BASE_API_URL}/markCandidateMessagesAsRead?applicationId=${app.applicationId}`); } catch (error) { }
    const unread = { ...unreadMessages, [app.applicationId]: 0 };
    setUnreadMessages(unread);
    setChatData({ applicationId: app.applicationId, candidateId: app.candidateId, hrId: app.hrId });
    setIsChatOpen(!isChatOpen);
  };

  const handleQAClick = (qa) => { setSelectedQA(qa); setShowQAModal(true); };

  // ------------------- Render -------------------
  return (
    <div className="main-content">
      {/* Filter & Search UI */}
      {/* ... your existing filter/search UI here ... */}
       {/* <Row className="d-flex justify-content-between align-items-center" style={{ marginBottom: '20px' }}>
          <Col xs="auto">
            <h2 className='text-start' style={{ paddingLeft: '12px' }}>Dream Applications</h2>
          </Col>
          <Col xs="auto">
            <GlobalHRSettings />
          </Col>
        </Row> */}
      <Row className="mb-4 m-3">
        {/* Filter by Status */}
        <Col xs={12} md={6} lg={4}>
          <label
            htmlFor="status"
            className="form-label"
            style={{ color: '#6c5b7b' }} // Purple color for the label
          >
            Filter by Status:
          </label>
          <select
            id="status"
            onChange={handleFilterChange}
            value={filterStatus}
            className="form-select form-select-sm fs-5"
            style={{
              borderColor: '#6c5b7b',
              borderRadius: '6px',
              height: '40px', // Match the height of the select
              width: '100%', // Full width
              padding: '0 10px', // Add horizontal padding for text
            }}            >
            <option value="all">All</option>
            <option value="Shortlisted">Shortlisted</option>
            <option value="Not Seen">Not Seen</option>
            <option value="Not Shortlisted">Not Shortlisted</option>
          </select>
        </Col>

        {/* Filter by Date */}
        <Col xs={12} md={6} lg={4}>
          <label
            htmlFor="date"
            className="form-label"
            style={{ color: '#6c5b7b' }} // Purple color for the label
          >
            Filter by Date:
          </label>
          <div className="date-filter d-flex flex-column flex-md-row gap-3">
            <div className="date-input-group d-flex flex-row gap-2">
              <div className="d-flex flex-column flex-md-row">
                <label
                  htmlFor="fromDate"
                  className="form-label mb-1"
                  style={{ color: '#6c5b7b' }} // Purple color for the label
                >
                  From:
                </label>
                <input
                  type="date"
                  id="fromDate"
                  value={fromDate}
                  onChange={(e) => handleFromDateChange(e.target.value)}
                  className="form-control form-control-sm fs-7"
                  style={{
                    maxWidth: '150px',
                    borderColor: '#6c5b7b',
                    boxShadow: 'none',
                    width: '100%', // Full width on mobile
                  }}
                />
              </div>
              <div className="d-flex flex-column flex-md-row">
                <label
                  htmlFor="toDate"
                  className="form-label mb-1"
                  style={{ color: '#6c5b7b' }} // Purple color for the label
                >
                  To:
                </label>
                <input
                  type="date"
                  id="toDate"
                  value={toDate}
                  onChange={(e) => handleToDateChange(e.target.value)}
                  className="form-control form-control-sm fs-7"
                  style={{
                    maxWidth: '150px',
                    borderColor: '#6c5b7b',
                    boxShadow: 'none',
                    width: '100%', // Full width on mobile
                  }}
                />
              </div>
            </div>
          </div>
        </Col>
      </Row>

      <Row style={{ marginLeft: '10px' }}>
        <p>
          <b>Action:(&nbsp;
            <span className="circle gray"></span>Not Seen,&nbsp;
            <span className="circle blue"></span>Seen,&nbsp;
            <span className="circle red"></span>Not Shortlisted,&nbsp;
            <span className="circle green"></span>Shortlisted)
          </b>
        </p>
      </Row>


      <Row className="mb-4 m-3">
        {/* Header Section */}
        <Col xs={12} md={6}>
          <h2 style={{ textAlign: 'center' }}>
            {loading ? (
              <div className="d-flex justify-content-center">
                <div className="spinner-bubble spinner-bubble-primary m-5" />
              </div>
            ) : applications.length === 0 ? (
              <div style={{ color: 'red', textAlign: 'center' }}>
                {search
                  ? `There is no Dream job application for "${search}"`
                  : 'Sorry, you haven’t received any applications yet.'}
              </div>
            ) : (
              <div className="left-text">
                Applicants of Dream Company Applications
              </div>
            )}
          </h2>
        </Col>

        {/* Search Bar Section */}
        <Col xs={12} md={6} className="d-flex justify-content-end">
          <div className="search-bar" style={{ width: '100%', maxWidth: '400px' }}>
            <input
              style={{
                borderColor: '#6c5b7b',
                borderRadius: '6px',
                height: '40px', // Match the height of the select
                width: '100%', // Full width
                padding: '0 10px', // Add horizontal padding for text
              }}
              type="text"
              name="search"
              placeholder="Search Candidate By Skills"
              value={search}
              onChange={handleSearchChange}
            />
          </div>
        </Col>
      </Row>




      {!loading && applications.length > 0 && (
        <Card body>
          <div className="table-details-list table-wrapper">
            <Table hover responsive>
              <thead>
                <tr>
                  <th>Candidate Name</th>
                  <th>Candidate Email</th>
                  <th>Resume ID</th>
                  <th>Date</th>
                  <th>Action</th>
                  <th>Note</th>
                  <th>Chat</th>
                  <th>QA</th>
                </tr>
              </thead>
              <tbody>
                {applications.map((item) => {
                  const app = item.application;
                  const qa = item.qusAns;
                  return (
                    <tr key={app.applicationId}>
                      <td>{isGetUserData ? 'Loading...' : app.candidateName || candidateName[app.candidateId]}</td>
                      <td
                        onClick={() => handleClick(app.candidateId)}
                        style={{ color: 'purple', padding: '10px', borderRadius: '5px', cursor: 'pointer', textDecoration: 'underline' }}
                      >
                        {isGetUserData ? 'Loading...' : app.candidateEmail || candidateEmail[app.candidateId]}
                      </td>
                      <td>{isLoading ? 'Loading...' : renderResumeComponent(app.resumeId)}</td>
                      <td>{app.appliedOn}</td>
                      <td style={{ textAlign: 'start', verticalAlign: 'middle' }}>
                        {app.applicationStatus === "Shortlisted" && hrEmail[app.hrId] !== userEmail ? (
                          <div>Shortlisted by {hrName[app.hrId] || 'Unknown HR'} of {app.companyName}</div>
                        ) : (
                          <Slider initialStatus={app.applicationStatus} onChangeStatus={(newStatus) => updateStatus(app.applicationId, newStatus, app.hrId)} />
                        )}
                      </td>
                      <td onClick={() => handleNoteClick(app.applicationId, app.candidateId, app.candidateName, app.resumeId)} style={{ cursor: 'pointer' }}>
                        <FaBookOpen />
                      </td>
                      <td>
                        {app.applicationStatus === "Shortlisted" ? (
                          <div style={{ position: 'relative', display: 'inline-block' }}>
                            {unreadMessages[app.applicationId] > 0 && (
                              <span style={{
                                position: 'absolute', top: '-5px', right: '-15px', backgroundColor: 'red',
                                color: 'white', borderRadius: '50%', width: '20px', height: '20px',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: '12px', fontWeight: 'bold', zIndex: 1
                              }}>
                                {unreadMessages[app.applicationId]}
                              </span>
                            )}
                            <SiImessage size={25} onClick={() => toggleChat(item)} style={{ color: 'green', cursor: 'pointer' }} />
                          </div>
                        ) : <SiImessage size={25} style={{ color: 'grey', cursor: 'not-allowed' }} />}
                      </td>
                      <td>
                        {qa && <FaQuestionCircle style={{ cursor: 'pointer', color: '#6c5b7b' }} size={20} onClick={() => handleQAClick(qa)} />}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
          <Pagination page={page} pageSize={pageSize} totalPages={totalPages} handlePageSizeChange={handlePageSizeChange} handlePageClick={handlePageClick} />
        </Card>
      )}

      {/* QA Modal */}
      <Modal show={showQAModal} onHide={() => setShowQAModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Candidate Responses</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div>
            <p><b>Why Apply:</b> {selectedQA.whyApply}</p>
            <p><b>Why Dream:</b> {selectedQA.whyDream}</p>
            <p><b>Why Fit:</b> {selectedQA.whyFit}</p>
          </div>
        </Modal.Body>
      </Modal>

      {/* Chat Component */}
      {isChatOpen && (
        <ChatComponent
          applicationId={chatData.applicationId}
          candidateId={chatData.candidateId}
          hrEmail={userEmail}
          userType='HR'
          setIsChatOpen={setIsChatOpen}
          isResumeBrief={isResumeBrief}
        />
      )}

      {/* Note Component */}
      {isNoteOpen && (
        <Note
          applicationId={selectedApplicationId}
          userName={userName}
          userEmail={userEmail}
          candidateName={selectedCandidateName}
          setIsNoteOpen={setIsNoteOpen}
        />
      )}

      {/* Brief Modal */}
      {showBriefSettings && (
        <Modal show={showBriefSettings} onHide={() => setShowBriefSettings(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Brief Resume</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div>{showMessage}</div>
          </Modal.Body>
        </Modal>
      )}
    </div>
  );
};

export default DreamApplication;
